<template>
    <div>
        <form-select v-model="form.date"
                     label="Available Dates"
                     name="event_date"
                     id="event_date"
                     :options="event.dates"
                     display-by="formatted_start_date"
                     :track-as-integer="true"
                     :bold="true"
                     class="mt-4"
        ></form-select>

        <available-seats :total="availableSeats.total"
                         :member="availableSeats.member"
                         v-if="! dateIsClosed"
        ></available-seats>

        <span class="inline-flex mt-1 w-full justify-center items-center px-2.5 py-1 rounded-md text-sm font-medium leading-5 bg-red-500 text-white transition-colors duration-300"
              v-else
        >
            <span class="block text-center">
                Event Closed For Bookings
            </span>
        </span>

        <div v-if="availableSeats.total > 0 || dateIsClosed">
            <form-select v-model="form.seats"
                         label="How many Seats"
                         name="seats"
                         id="seats"
                         :options="seatOptions"
                         :track-as-integer="true"
                         :bold="true"
                         class="mt-3"
                         :disabled="availableSeats.total === 0 || dateIsClosed"
            ></form-select>

            <form-button class="mt-4"
                         :disabled="availableSeats.total === 0 || dateIsClosed"
                         @click.native="showOrAdd"
            >
                Book Now
            </form-button>
        </div>

        <div v-else>
            <div v-if="initialised">
                <event-waiting-list :event-date-id="form.date"
                                    :event-name="event.name"
                                    :event-date="selectedDate.start_date_no_time"
                ></event-waiting-list>
            </div>
        </div>

        <portal to="slideover">
            <slide-over heading="Event Add-ons"
                        v-if="showAddOns"
                        @hide="showAddOns = false; initialiseFormData()"
            >
                <div class="flex flex-col justify-between min-h-full px-4 sm:px-6">
                    <div class="divide-y divide-gray-200">
                        <div class="py-4"
                             v-for="(addon, index) in addOns"
                        >
                            <div class="flex items-center">
                                <h3 class="mb-1 mr-1" v-html="addon.name"></h3>

                                <button class="-mt-2"
                                        @click="modal.heading = addon.name; modal.description = addon.description"
                                        v-if="addon.description"
                                >
                                    <svg class="w-5 h-5 hover:text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                </button>
                            </div>

                            <form-select v-model="form.addOns[index].value"
                                         :options="addon.options"
                                         :track-as-integer="true"
                                         class="mt-2"
                                         v-if="addon.applies_to_group"
                            ></form-select>

                            <form-multi-add-on v-model="form.addOns[index].value"
                                               :options="addon.options"
                                               :group-max="form.seats"
                                               class="mt-2"
                                               v-else
                            >
                            </form-multi-add-on>
                        </div>
                    </div>

                    <div class="border-t border-gray-200 -mx-4 sm:-mx-6 py-4 px-4 sm:px-6">
                        <h3 class="mb-1">Booking Summary</h3>
                        <booking-summary :event="event"
                                         :seats="form.seats"
                                         :selected-add-ons="form.addOns"
                                         :add-on-options="addOnOptions"
                        ></booking-summary>
                    </div>
                </div>

                <template v-slot:footer="slotProps">
                    <div class="flex-shrink-0 px-4 py-4 sm:space-x-4 flex flex-col-reverse sm:flex-row sm:justify-end">
                        <form-button colour="gray"
                                     class="w-full sm:w-auto"
                                     @click.native="slotProps.hide()"
                        >
                            Cancel
                        </form-button>
                        <form-button class="w-full sm:w-auto"
                                     @click.native="addToBasket(slotProps.hide())"
                        >
                            Add to Basket
                        </form-button>
                    </div>
                </template>
            </slide-over>
        </portal>

        <portal to="modal">
            <modal @hide="modal.heading = ''; modal.description = ''"
                   v-if="modal.heading"
            >
                <template v-slot:default="props">
                    <h3 class="mb-1" v-html="modal.heading"></h3>
                    <div class="text-gray-600 space-y-3"
                         v-html="modal.description"
                    ></div>

                    <div class="flex justify-end -mb-2 sm:-mb-4">
                        <form-button color="gray"
                                     @click.native="props.hide"
                        >
                            Close
                        </form-button>
                    </div>
                </template>
            </modal>
        </portal>
    </div>
</template>

<script>
    import FormSelect from "../../form/Select";
    import FormButton from "../../form/public/Button";
    import FormMultiAddOn from "./MultiAddOn";
    import AvailableSeats from "../../AvailableSeats";
    import Modal from "../../Modal";
    import EventWaitingList from "../../EventWaitingList";

    import BookingSummary from "./Summary";

    import SlideOver from "../../SlideOver";

	export default {
	    data() {
	        return {
                showAddOns: false,
                availableSeats: {
                    total: 0,
                    member: 0
                },

                form: {
                    event: null,
                    date: null,
                    seats: 1,
                    addOns: {},
                },

                modal: {
                    heading: '',
                    description: '',
                },

                initialised: false,
            }
        },

	    props: {
	        event: {
	            type: Object,
                required: true,
            },

            addOns: {
	            type: Array,
                required: true,
            }
        },

        components: { FormSelect, FormButton, FormMultiAddOn, BookingSummary, SlideOver, AvailableSeats, Modal, EventWaitingList },

        watch: {
	        'form.date': function (value) {
                axios.get(`/available-seats/${value}`)
                    .then(response => {
                        this.availableSeats = response.data;
                        this.initialised = true;
                    })
            }
        },

        created() {
	        let url = new URLSearchParams(window.location.search);

	        this.form.event = this.event.id;

            if (url.has('event_date')) {
                this.form.date = parseInt(url.get('event_date'));
            } else {
	            this.form.date = _.first(this.event.dates).id;
            }

	        this.initialiseFormData();
        },

        computed: {
	        seatOptions() {
	            return _.map(_.range(1, this.availableSeats.total + 1), numberOfSeats => {
	                return { name: numberOfSeats, id: numberOfSeats };
                })
            },

            addOnOptions() {
	            return _.flatMap(this.addOns, 'options');
            },

            selectedDate() {
	            return _.find(this.event.dates, ['id', this.form.date]);
            },

            dateIsClosed() {
	            let selectedDate = _.find(this.event.dates, ['id', this.form.date]);

	            if (! selectedDate) return true;

	            return selectedDate.is_closed_to_booking;
            }
        },

        methods: {
	        initialiseFormData() {
                _.forEach(this.addOns, (addon, index) => {
                    let value;

                    if (addon.applies_to_group) {
                        value = _.first(addon.options).id;
                    } else {
                        value = _.map(addon.options, option => {
                            return {
                                option_id: option.id,
                                value: 0,
                            }
                        })
                    }

                    this.$set(this.form.addOns, index, {
                        addon_id: addon.id,
                        name: addon.name,
                        value: value,
                        applies_to_group: addon.applies_to_group
                    })
                });
            },

            addToBasket(hide=null) {
	            axios.post('/basket/event', this.form)
                    .then(response => {
                        bus.$emit('update-basket-count');
                        setTimeout(() => {
                            createToast('Event added to basket!', `The ${this.event.name} event was added to the basket.`);
                        }, 500);

                        if (hide) {
                            hide();
                        }
                    })
                    .catch(error => {

                    });
            },

            showOrAdd() {
	            if (this.addOns.length > 0) {
	                return this.showAddOns = true;
                }

                this.addToBasket();
            }
        }
    }
</script>
